import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { HiXMark } from "react-icons/hi2";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  header?: string;
  subHeader?: string;
  children: React.ReactNode;
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideUp = keyframes`
  from { transform: translateY(50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const slideDown = keyframes`
  from { transform: translateY(0); opacity: 1; }
  to { transform: translateY(50px); opacity: 0; }
`;

const Overlay = styled.div<{ state: "open" | "closing" | "closed" }>`
  display: ${({ state }) => (state === "closed" ? "none" : "flex")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;

  ${({ state }) =>
    state === "open"
      ? css`
          animation: ${fadeIn} 0.25s ease forwards;
        `
      : state === "closing"
      ? css`
          animation: ${fadeOut} 0.25s ease forwards;
        `
      : ""}
`;

const Content = styled.div<{ state: "open" | "closing" | "closed" }>`
  background: rgba(8, 8, 8, 1);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  min-width: 570px;
  min-height: 446px;

    @media (max-width: 768px) {
    min-height: 20vh;
    min-width: 87.5vw;
  }

  position: relative;

  ${({ state }) =>
    state === "open"
      ? css`
          animation: ${slideUp} 0.25s ease forwards;
        `
      : state === "closing"
      ? css`
          animation: ${slideDown} 0.25s ease forwards;
        `
      : ""}
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-color);
  font-size: 18px;
  cursor: pointer;
`;

const Header = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  justify-content: space-between;
`;

const SubHeader = styled.h3`
  padding: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: var(--text-secondary-color);
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  header,
  subHeader,
  children,
}) => {
  const [state, setState] = useState<"open" | "closing" | "closed">("closed");

  useEffect(() => {
    if (isOpen) {
      setState("open");
    } else if (!isOpen && state === "open") {
      setState("closing");
      const t = setTimeout(() => setState("closed"), 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (state === "closed") return null;

  return (
    <Overlay state={state} onClick={onClose}>
      <Content
        state={state}
        onClick={(e) => e.stopPropagation()}
      >
        <HeaderContainer>
          {header && (
            <Header>
              {header}
              <CloseButton onClick={onClose}>
                <HiXMark size={24} />
              </CloseButton>
            </Header>
          )}
          {subHeader && <SubHeader>{subHeader}</SubHeader>}
        </HeaderContainer>

        {children}
      </Content>
    </Overlay>
  );
};
